import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ensureLanguageLoaded, notifyLocaleChanged, t } from '../i18n/uiText';
import { sortLanguagesByDisplayOrder } from '../utils/sortLanguages';
import { FALLBACK_UI_LANGUAGE, rememberLanguageChoice } from '../utils/localeBootstrap';
import { authService } from '../services/auth';
import { telegramService } from '../services/telegram';

const API_BASE = import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';

const READING_FONT_KEY = 'bibleReadingFont';
const READING_FONT_DEFAULT = 'book';
const READING_FONT_ALLOWED = new Set(['book', 'serif_alt', 'alegreya', 'sans']);
const READING_FONT_OPTIONS = [
  { id: 'book', label: 'Literata', hint: 'Serif, best for long reading' },
  { id: 'serif_alt', label: 'Alt Serif', hint: 'Higher contrast serif style' },
  { id: 'alegreya', label: 'Alegreya', hint: 'Warm literary serif with character' },
  { id: 'sans', label: 'Inter Sans', hint: 'Clean modern interface' },
];

function applyReadingFontToBody(fontId) {
  if (typeof document === 'undefined') return;
  const safeFont = READING_FONT_ALLOWED.has(fontId) ? fontId : READING_FONT_DEFAULT;
  document.body.setAttribute('data-reading-font', safeFont);
}

export default function Settings() {
  const navigate = useNavigate();
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settingsTab, setSettingsTab] = useState('language');
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    return localStorage.getItem('bibleLanguage') || FALLBACK_UI_LANGUAGE;
  });
  const [selectedReadingFont, setSelectedReadingFont] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(READING_FONT_KEY) || READING_FONT_DEFAULT;
    }
    return READING_FONT_DEFAULT;
  });

  useEffect(() => {
    async function loadLanguages() {
      try {
        const response = await fetch(`${API_BASE}/languages`);
        if (!response.ok) throw new Error('Failed to load languages');
        const data = await response.json();
        setLanguages(sortLanguagesByDisplayOrder(data.languages || []));
      } catch (error) {
        console.error('Error loading languages:', error);
      } finally {
        setLoading(false);
      }
    }
    loadLanguages();
  }, []);

  const handleSelectReadingFont = (fontId) => {
    setSelectedReadingFont(fontId);
    if (typeof window === 'undefined') return;
    localStorage.setItem(READING_FONT_KEY, fontId);
    applyReadingFontToBody(fontId);
    window.dispatchEvent(new CustomEvent('verbum:reading-font-change', { detail: { font: fontId } }));
  };

  const handleSelectLanguage = async (lang) => {
    await ensureLanguageLoaded(lang.code);
    setSelectedLanguage(lang.code);
    rememberLanguageChoice(lang.code);
    notifyLocaleChanged();
    if (lang.defaultTranslation) {
      localStorage.setItem('bibleTranslation', lang.defaultTranslation);
    }
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        await fetch(`${API_BASE}/me/preferred-language`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ language: lang.code }),
        });
      } catch (e) {
        console.error('Failed to save preferred language', e);
      }
    }
  };

  const handleBack = () => {
    const translation = localStorage.getItem('bibleTranslation') || 'kjv';
    navigate(`/${translation}`);
  };

  const handleLogout = async () => {
    authService.clearSessionOnLogout();

    if (telegramService.isTelegramWebApp) {
      try {
        const user = telegramService.getUser();
        if (user?.id) {
          await authService.authenticateWithTelegram(user);
          const newToken = authService.getAuthToken();
          if (newToken) {
            window.location.reload();
            return;
          }
        }
      } catch (e) {
        console.error('Telegram re-login after logout failed:', e);
      }
    }

    const translation = localStorage.getItem('bibleTranslation') || 'kjv';
    window.location.href = `/${translation}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-slate-200 border-t-slate-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-semibold">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200 shadow-sm">
        <header className="flex items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
            aria-label={t('settingsBackAria')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-semibold text-slate-700 flex-1">{t('settingsTitle')}</h1>
        </header>

        <div className="flex shrink-0 border-t border-slate-100 px-2" role="tablist" aria-label={t('settingsTitle')}>
          <button
            type="button"
            role="tab"
            aria-selected={settingsTab === 'language'}
            className={`flex-1 py-3 text-sm font-semibold transition-colors border-b-2 -mb-px ${
              settingsTab === 'language'
                ? 'border-slate-600 text-slate-800'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setSettingsTab('language')}
          >
            {t('appLanguage')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={settingsTab === 'font'}
            className={`flex-1 py-3 text-sm font-semibold transition-colors border-b-2 -mb-px ${
              settingsTab === 'font'
                ? 'border-slate-600 text-slate-800'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setSettingsTab('font')}
          >
            {t('readingFont')}
          </button>
        </div>
      </div>

      <main className="flex-1 p-4 max-w-2xl mx-auto w-full">
        {settingsTab === 'language' && (
          <section className="mb-6">
            <p className="text-slate-600 text-sm mb-4 leading-relaxed">{t('settingsLanguageHelp')}</p>
            <ul className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50 shadow-sm">
              {languages.map((lang) => (
                <li key={lang.code}>
                  <button
                    type="button"
                    onClick={() => handleSelectLanguage(lang)}
                    className={`w-full flex items-center justify-between px-4 py-3.5 text-left transition-colors border-b border-slate-200 last:border-b-0 ${
                      selectedLanguage === lang.code
                        ? 'bg-slate-100 text-slate-700'
                        : 'bg-transparent text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <span className="font-medium">{lang.name}</span>
                    {selectedLanguage === lang.code && (
                      <svg className="w-5 h-5 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                  {selectedLanguage === lang.code && lang.translations?.length > 1 && (
                    <div className="px-4 pb-3 pt-1 bg-slate-100 border-t border-slate-200">
                      <p className="text-xs text-slate-500 leading-snug">
                        {t('settingsTranslationsLabel')}: {lang.translations.map((tr) => tr.name).join(', ')}
                      </p>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {settingsTab === 'font' && (
          <section className="mb-6">
            <p className="text-slate-600 text-sm mb-4 leading-relaxed">{t('settingsReadingFontHelp')}</p>
            <ul className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50 shadow-sm">
              {READING_FONT_OPTIONS.map((font) => (
                <li key={font.id}>
                  <button
                    type="button"
                    onClick={() => handleSelectReadingFont(font.id)}
                    className={`w-full flex items-center justify-between px-4 py-3.5 text-left transition-colors border-b border-slate-200 last:border-b-0 ${
                      selectedReadingFont === font.id
                        ? 'bg-slate-100 text-slate-700'
                        : 'bg-transparent text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <span>
                      <span className="block font-medium">{font.label}</span>
                      <span className="block text-xs text-slate-500 mt-0.5 opacity-90">{font.hint}</span>
                    </span>
                    {selectedReadingFont === font.id && (
                      <svg className="w-5 h-5 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mb-6">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 hover:bg-rose-100 transition-colors"
          >
            {t('settingsLogout')}
          </button>
        </section>
      </main>
    </div>
  );
}
